import { Component} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ColumnDefinition } from '../../models/dynamic-table/dynamic-table-data.model';
import { DynamicTableService } from '../../services/dynamic-table/dynamic-table.service';
import { AppMainService } from 'src/app/app-main.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { catchError, Observable, of, Subject, takeUntil } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { DynamicTableFormService } from '../../services/dynamic-table/dynamic-table-form.service';
import { TimeZoneService } from '../../services/timezone.service';
import { environment } from 'src/environments/environment';
import { DocumentManagementService } from '../../services/document-management/document-management.service';
import { validateFiles, validateFileSize } from 'src/app/shared/upload-files';
import { FormControlListIds } from '../../constants/formcontrol-list.constants';
import { DynamicFormStatus } from '../../enums/workbook/dynamic-form-status.enum';
import { DocumentViewerService } from '../../services/document-viewer.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';


@Component({
  selector: 'app-dynamic-form-external',
  templateUrl: './dynamic-form-external.component.html',
  styleUrl: './dynamic-form-external.component.scss'

})
export class DynamicFormExternalComponent {

  /** Display Sections */
  displayAuthenticationModal: boolean;
  displayForm: boolean = false;
  displaySubmissionMessage: boolean = false;
  
  /**Dynamic Form */
  isFormSubmitted: boolean = false
  formId: string;
  dynamicFormData: any;
  columnDefinitions: ColumnDefinition[];
  submissionMessage: string = '';
  formHeader: string = 'Survey Form';
  workbookId: string;
  form: FormGroup;
  // isFormChanged: boolean = false; 
  password:string;
  assestBaseUrl: string = environment.assestsBasePath;
  usersList: any;
  allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif'];
  multiLineTooltipPicture = `<ul class="p-mt-0 p-pl-4 p-mb-0">
  <li>Files with extensions: gif, png, jpg, jpeg are allowed</li>
  <li>Files size should not be more than 5MB</li>
</ul>`;
  multiLineTooltipFile = `<ul class="p-mt-0 p-pl-4 p-mb-0">
  <li>Files with extensions: gif, png, jpg, jpeg, pdf, txt, xls, xlsx, doc, docx, ppt, pptx are allowed</li>
  <li>Files size should not be more than 5MB</li>
  </ul>`;
  value = 0; // Progress bar value
  /** Subscription and Cleanup */
  protected ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public dynamicTableService: DynamicTableService,
    private readonly appMainService: AppMainService,
    private readonly messageService: MessageService,
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    public dynamicTableFormService: DynamicTableFormService,
    private readonly timezoneService: TimeZoneService,  
    private readonly documentManagementService: DocumentManagementService,
    private readonly confirmationService: ConfirmationService, 
    private documentViewerService: DocumentViewerService,
    private sanitizer: DomSanitizer,

  ) { 
    // this.usersList = this.app.usersList;
    this.changeThemeSettings();

  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.workbookId = params.get('workbookid') || '';
      this.formId = params.get('formid') || '';
      this.getExistingForm(this.formId)
    });
  }


  onAuthenticationSubmit(password: string) {
    this.isFormSubmitted = true;
    this.dynamicTableFormService.validateFormAuthentication(this.formId, password).subscribe({
      next: (response) => {
        this.isFormSubmitted = false;
        if (response === true) {
          this.displayAuthenticationModal = false; 
          this.displayForm = true; 
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Authentication Failed',
            detail: 'Incorrect password. Please try again.'
          });
        }
      },
      error: (error) => {
        this.isFormSubmitted = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message || 'Authentication failed due to an API error.'
        });
      }
    });
  }

  getExistingForm(formID: string) {
    this.appMainService.isShowSpinner('getExistingForm');
    this.dynamicTableFormService.getDynamicForm(formID).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('getExistingForm');
        this.dynamicFormData = response;
        if(this.dynamicFormData.status === DynamicFormStatus.InActive || this.dynamicFormData.status === DynamicFormStatus.Deleted){
          this.displaySubmissionMessage = true;
          this.submissionMessage = 'Form is not available';
          return;     
        }
        this.columnDefinitions = response.columns;
        // this.initializePicklistValues();
        if (this.dynamicFormData.authenticationId === 0 ||this.dynamicFormData.authenticationId === null ) {
          this.displayForm = true;
        } else if (this.dynamicFormData.authenticationId === 1) {
          this.displayAuthenticationModal = true;
        }
        this.buildForm();
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getExistingForm');
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to load form data!' });
      }
    });
  }

  buildForm() {
    const group: any = {};
    this.columnDefinitions.forEach(column => {
      const control = this.createControl(column);
      group[column.id] = control;
    });

    this.form = this.fb.group(group);
    this.form.valueChanges.subscribe(() => {
      this.updateProgressBar();
    });

    // Subscribe to form value changes to track changes
    // this.form.valueChanges.subscribe(() => {
    //   this.isFormChanged = !this.form.pristine;
    // });
  }

  createControl(column: any) {
    const validators = [];
    if(column.isRequired && column.formControlName !== 'UserLookup' && column.formControlName !== 'ToggleButton') {
      validators.push(Validators.required);
    }
    
    if(column?.regularExpression && [FormControlListIds.Email,FormControlListIds.Phone,FormControlListIds.IPAddress,FormControlListIds.URL].includes(column.formControlId)){
      validators.push(Validators.pattern(column?.regularExpression));
    }     
    if (column.formControlName === 'RadioButton') {
      return this.fb.control(null, validators); // Single selection
    }
    else if (column.formControlName === 'FileUpload') {
      return this.fb.array([], validators); // Store uploaded file responses
    }
    else if (column.formControlName === 'Picture') {
      return this.fb.array([], validators); // Store uploaded file responses
    }
    else if (column.formControlName === 'CheckBox') {
      // Return a FormArray with one FormControl for each option, initialized to false
      if(column.isRequired){
        return new FormArray(
          column.pickListValues.map(() => new FormControl(false)),
          this.atLeastOneCheckboxRequired() 
        );
      }
      else{
        return new FormArray(
          column.pickListValues.map(() => new FormControl(false)),
        );
      }
    }
    return this.fb.control({ value: null, disabled: false }, validators);
  }
  
  updateProgressBar() {
    const totalFields = Object.keys(this.form.controls).length;
    const filledFields = Object.entries(this.form.controls).filter(([key, control]) => {
      const value = control.value;
      if (Array.isArray(value)) return value.some(v => v); // Handle checkboxes
      return value !== null && value !== '' && value !== undefined && control.valid; // Check if value is valid
    }).length;

    this.value = Math.round((filledFields / totalFields) * 100);
  }

  atLeastOneCheckboxRequired(): ValidatorFn {
    return (formArray: AbstractControl) => {
      const checked = (formArray as FormArray).controls.some(control => control.value);
      return checked ? null : { required: true }; // Error if none are selected
    };
  }

  onSubmit() {
    if (this.form.valid) {
      this.isFormSubmitted = true
      const newRowObject = this.mapNewRowData(this.form.value);
      this.createWorkbookRow(newRowObject);
    }
    else{
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please fill all required fields!' });
      this.form.markAllAsTouched();
    }
  }

  mapNewRowData(formData: { [key: string]: any }) {
    const cells = this.mapSubmitData(formData);
    return {
      tableId: this.workbookId,
      cells: cells.map(cell => ({
        columnId: cell.columnId,
        value: cell.value,
        values: cell.values,
      }))
    };
  }


  createWorkbookRow(newRow: any) {
    this.appMainService.isShowSpinner('createWorkbookRow');
    this.dynamicTableService.createWorkbookRow(this.workbookId, newRow).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(
      {
        next: (response: any) => {
          this.appMainService.isHideSpinner('createWorkbookRow');
          this.isFormSubmitted = false
          this.submissionMessage = 'Form is submitted successfully';
          this.displayForm = false;
          this.displaySubmissionMessage = true;

        },
        error: (error) => {
          this.appMainService.isHideSpinner('createWorkbookRow');
          this.isFormSubmitted = false;
          this.submissionMessage = 'Form is not submitted';
          this.displayForm = false;
          this.displaySubmissionMessage = true;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Form is not submitted!' });
        }
      },
    );
  }

  getWorkbookColumnDropdowns(columnId: string): Observable<any[]> {
    // Return the observable from the service
    return this.dynamicTableService.getWorkbookColumnDropdowns(columnId);
  }

  initializePicklistValues() {
    this.columnDefinitions?.forEach(column => {
      if (column?.formControlName === 'RadioButton' || column?.formControlName === 'CheckBox') {
        this.getWorkbookColumnOptionValue(column?.id).subscribe((pickListValues) => {
          column.pickListValues = pickListValues||[]; // Assign API response to pickListValues
        });
      }
    });
  }

  getWorkbookColumnOptionValue(columnId: string, query: string = ''): Observable<any[]> {
    return this.dynamicTableService.getWorkbookColumnOptionValue(columnId, query);
  }
  
    onDropdownSearch(column: ColumnDefinition, query: string) {
      const control = this.form.get(column.id);
      const searchQuery = query?.trim();
      if (column?.pickListValues?.length > 0 && !searchQuery) {
            column.isLoading = false;
            column.pickListValues = [...column?.pickListValues];
          return; // Return if pickListValues has length but query is falsy
      }
      else{     
        column.isLoading = true;
        this.getWorkbookColumnOptionValue(column.id, searchQuery).pipe(
          catchError(error => {
              column.isLoading = false;
              return of([]);
          })
      ).subscribe(data => {
          column.isLoading = false;
          const pickListValues = data.map(item => item?.value);
          column.pickListValues = pickListValues; 
    
          if (!(pickListValues && pickListValues.length > 0)) {
              control?.clearValidators(); // Remove all validators if no pickListValues
          }
    
          control?.updateValueAndValidity(); 
      });
      }
    }

    mapSubmitData(submitData: { [key: string]: any }, rowNumber?: number) {
      const mappedArray = [];
  
      for (let [columnId, value] of Object.entries(submitData)) {
        const columnDef = this.columnDefinitions.find(col => col.id === columnId);
        if (columnDef.formControlName === 'DatePicker' && value) {
          value = this.timezoneService._getCustomDateString(value)
        }
        else if (columnDef.formControlName === 'DateTimePicker' && value) {
          value = this.timezoneService._getCustomDateTimeString(value)
        }
        else if (columnDef.formControlName === 'DateTimePicker' && value) {
          value = this.timezoneService._getCustomDateTimeString(value)
        }
        else if ((columnDef.formControlName === 'FileUpload' || columnDef.formControlName === 'Picture')&& value) {
          value = JSON.stringify(value);
        }
        else if (columnDef.formControlName === 'ToggleButton') {
          if (value === true || value === false) {
            value = value.toString() || 'false';
          }else{
            value ='false';
          }
        }
        else if (columnDef.formControlName === 'AutoNumber') {
          value = null;
        }
        const dataObject: { columnId: string; value?: any; values?: any[]; rowNumber?: number } = {
          columnId
        };
    
        // Use 'values' instead of 'value' for MultiDropdown and Checkbox
        if (columnDef.formControlName === 'MultipleDropDownList') {
          if (Array.isArray(value)) {
            // Flatten the array and filter out null values
            dataObject.values = value
              .flat()
              .filter(item => item !== null && item !== undefined);
          } 
          // If it's a single value (not null)
          else if (value) {
            dataObject.values = [value];
          }
          else{
            dataObject.values = null;
          } 
        }
        else if (columnDef.formControlName === 'CheckBox') {
          let formArray = this.form.get(columnDef.id) as FormArray;
          
          if (formArray) {
            const selectedValues = columnDef.pickListValues.filter(
              (_, index) => formArray.at(index).value === true
            );
        
            if (selectedValues.length > 0) {
              dataObject.values = selectedValues;
            }
            else{
              dataObject.values = null;
            } 
          }
        }
        
         else {
          dataObject.value = value;
        }  
  
        // Only add rowNumber if it's passed
        if (rowNumber !== undefined) {
          dataObject.rowNumber = rowNumber;
        }
        mappedArray.push(dataObject);
      }
      return mappedArray;
    }


  onCustomUpload(event: any, field: any, fileUpload, type) {
    if (event?.files&& event?.files?.length>5) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Maximum 5 files are allowed at once!' });
      fileUpload.clear();
      return;
    }
    let errors = [];
    for (let file of event.files) {      
      this.appMainService.isShowSpinner('upload')
      if (type == 'image') {
          errors = validateFiles(file, 5, this.allowedExtensions);
      }else{
          errors = validateFileSize(file, 5);
      }

      if (errors.length) {
          for (let error of errors) {
              this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: error.fileName + ": " + error.error
              });
          }
          this.appMainService.isHideSpinner('upload');
      }
      this.appMainService.isHideSpinner('upload');    }
      if(errors.length){
        fileUpload.clear();
        return;
      }
    const uploadedFiles:File[] = Array.from(event.files || []); // Convert FileList to an array
    if (!Array.isArray(uploadedFiles)) {
      return;
    }
    const path = `workbook/${this.workbookId}/dynamic-form/${this.formId}`;
    this.bulkUploadDocumentForExternalFile(uploadedFiles, path, field.id);
    fileUpload.clear();
  }
  
  bulkUploadDocument(files: File[], path: string, fieldId) {
    this.appMainService.isShowSpinner('bulkUpload');
    this.documentManagementService.bulkUploadDocument(files, path)
  .pipe(takeUntil(this.ngUnsubscribe$))
  .subscribe({
    next: (response: any) => {
      this.appMainService.isHideSpinner('bulkUpload');
      if (response) {
        this.updateFormWithFiles(response, fieldId);
        this.form.get(fieldId)?.markAsPristine();
        this.form.markAsPristine();
        this.form.markAsDirty();
      }
    },
    error: (error) => {
      this.appMainService.isHideSpinner('bulkUpload');
      this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to upload documents!' });
    }
  });

  } 
  
  bulkUploadDocumentForExternalFile(files: File[], path: string, fieldId) {
    this.appMainService.isShowSpinner('bulkUploadDocumentForExternalFile');
    this.dynamicTableService.bulkUploadDocumentForExternalFile(files,this.dynamicFormData.organizationId, this.dynamicFormData.userId, path)
  .pipe(takeUntil(this.ngUnsubscribe$))
  .subscribe({
    next: (response: any) => {
      this.appMainService.isHideSpinner('bulkUploadDocumentForExternalFile');
      if (response) {
        this.updateFormWithFiles(response, fieldId);
        this.form.get(fieldId)?.markAsPristine();
        this.form.markAsPristine();
        this.form.markAsDirty();
      }
    },
    error: (error) => {
      this.appMainService.isHideSpinner('bulkUploadDocumentForExternalFile');
      this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to upload documents!' });
    }
  });

  } 
  
  // Update form control with API response
  updateFormWithFiles(files: any[], fieldId: string) {
    const formArray = this.form.get(fieldId) as FormArray;
    files.forEach(file => {
      formArray.push(new FormControl(file)); // Store file info
    });
  }
  
  
  downloadFile(file, event, isDownload=false) {
    if(event){
      event.stopPropagation();
    }
    const documentPayload = {
      id: file?.id,
      isDefaultContentType: isDownload? true: false,
      expirationTimeInMinutes: 5,
      organizationId: this.dynamicFormData?.organizationId,
      userId: this.dynamicFormData?.userId
    }
    this.appMainService.isShowSpinner('downloadFile');
    this.dynamicTableService.downloadDocumentForExternalFile(documentPayload).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('downloadFile');
        if(isDownload){
          const fileUrl = response?.downloadLink; // Replace with your API's file URL field
          if (fileUrl) {
            window.open(fileUrl, '_blank'); // Open the file in a new tab
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Document Download successfully!' });
          } else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'File URL is not available' });
          }
        }
        else {
          const getFileExtension = (filename: string) => filename.split('.').pop() || '';
          const extension = getFileExtension(file?.fileName);
          this.documentViewerService.previewDocument(extension, response);
        }
      },
      error: (error) => {
        this.appMainService.isHideSpinner('downloadFile');
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'File URL is not available' });
      }
    })
  }
  
  deleteDocument(selectedFile: any, column, index) {
    this.confirmationService.confirm({
      message: 'Do you want to delete this Document?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.appMainService.isShowSpinner('deleteDocument');
        this.dynamicTableService.deleteDocumentForExternalFile(selectedFile?.id, this.dynamicFormData?.organizationId, this.dynamicFormData?.userId).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
          next: (response: any) => {
            this.appMainService.isHideSpinner('deleteDocument');
            const formArray = this.form.get(column.id) as FormArray;
            formArray.removeAt(index);        
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Document deleted successfully!' });
          },
          error: (error) => {
            this.appMainService.isHideSpinner('deleteDocument');
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to delete document!' });
          }
        })
      },
      reject: () => { },
    });
  }
  clearRating(columnId, event){
    this.form.get(columnId).setValue(null);   
    this.form.markAsDirty();
    this.form.markAsTouched();
    this.form.updateValueAndValidity();
  }



  changeThemeSettings() {
    const theme = "teal";
    const themeLink: HTMLLinkElement = document.getElementById(
        "theme-css"
    ) as HTMLLinkElement;
    const href =
        "assets/theme/theme-" + theme + ".css";
    this.replaceLink(themeLink, href);
}

rawHTML(content: string): SafeHtml{
  return content ?  this.sanitizer.bypassSecurityTrustHtml(content): ''
}

replaceLink(linkElement, href) {
  if (this.isIE()) {
      linkElement.setAttribute("href", href);
  } else {
      const id = linkElement.getAttribute("id");
      const cloneLinkElement = linkElement.cloneNode(true);

      cloneLinkElement.setAttribute("href", href);
      cloneLinkElement.setAttribute("id", id + "-clone");

      linkElement.parentNode.insertBefore(
          cloneLinkElement,
          linkElement.nextSibling
      );

      cloneLinkElement.addEventListener("load", () => {
          linkElement.remove();
          cloneLinkElement.setAttribute("id", id);
      });
  }
}

isIE() {
  return /(MSIE|Trident\/|Edge\/)/i.test(window.navigator.userAgent);
}

  ngOnDestroy(): void {
    if (this.ngUnsubscribe$) {
      this.ngUnsubscribe$.next();
      this.ngUnsubscribe$.complete();
      this.ngUnsubscribe$.unsubscribe();
    }
  }
}
